import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Shield, Copy, Check, ArrowRight, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function Admin2FASetup() {
  const navigate = useNavigate();
  const location = useLocation();

  const tempToken = location.state?.temp_token || "";
  const [step, setStep] = useState("qr"); // qr → verify → recovery → done
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!tempToken) {
      navigate("/login", { replace: true });
      return;
    }
    // Fetch QR code
    api.post("/admin/2fa/setup", { temp_token: tempToken })
      .then(({ data }) => {
        setQrCode(data.qr_code);
        setSecret(data.secret);
      })
      .catch(() => {
        toast.error("Failed to initialize 2FA setup");
        navigate("/login", { replace: true });
      });
  }, [tempToken, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/admin/2fa/enable", {
        temp_token: tempToken,
        code,
      });
      setRecoveryCodes(data.recovery_codes || []);
      setStep("recovery");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid code. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    // 2FA is now enabled on the account. The existing session (from the
    // forced-setup temp_token) is not privileged — admin must sign in again
    // through the dedicated admin portal so the new TOTP is exercised and a
    // full tfa_verified JWT is issued.
    navigate("/housekeeping/login", {
      replace: true,
      state: { message: "2FA enabled. Please sign in with your authenticator code." },
    });
  };

  const copyRecoveryCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      setCopied(true);
      toast.success("Recovery codes copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Clipboard unavailable — select and copy manually");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F5F5] px-4" data-testid="admin-2fa-setup">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-xl bg-red-600 flex items-center justify-center mx-auto mb-4">
            <Shield size={24} className="text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-[#111111] tracking-tight">
            Admin 2FA Setup
          </h1>
          <p className="text-sm text-[#888888] mt-1">
            Two-factor authentication is required for admin access
          </p>
        </div>

        {/* Step: QR Code */}
        {step === "qr" && (
          <div className="bg-white rounded-xl border border-[#E5E5E5] p-6 space-y-5" data-testid="2fa-qr-step">
            <div className="text-center space-y-3">
              <p className="text-sm text-[#555555]">
                Scan this QR code with your authenticator app
              </p>
              <p className="text-xs text-[#888888]">
                Google Authenticator, Authy, 1Password, or similar
              </p>
            </div>

            {qrCode ? (
              <div className="flex justify-center">
                <img
                  src={qrCode}
                  alt="2FA QR Code"
                  className="w-48 h-48 rounded-lg border border-[#E5E5E5]"
                  data-testid="2fa-qr-image"
                />
              </div>
            ) : (
              <div className="flex justify-center h-48 items-center">
                <div className="w-6 h-6 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {secret && (
              <div
                className="rounded-lg border border-blue-200 bg-blue-50/60 p-4 space-y-3"
                data-testid="2fa-manual-key"
              >
                <div className="flex items-start gap-2">
                  <KeyRound size={16} className="text-blue-600 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-blue-700">
                      Or enter this code manually
                    </p>
                    <p className="text-xs text-[#555555] mt-0.5">
                      Enter this key in your authenticator app to generate login codes.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 sm:items-stretch">
                  <div className="flex-1 rounded-md border-2 border-dashed border-blue-300 bg-white px-4 py-3">
                    <code
                      className="block font-mono text-base sm:text-lg tracking-wider text-[#111111] break-all select-all text-center"
                      data-testid="2fa-manual-key-value"
                    >
                      {secret}
                    </code>
                  </div>
                  <Button
                    type="button"
                    className="h-auto sm:w-28 bg-blue-600 hover:bg-blue-700 text-white shrink-0"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(secret);
                        setCopied(true);
                        toast.success("Key copied");
                        setTimeout(() => setCopied(false), 2000);
                      } catch {
                        toast.error("Clipboard unavailable — tap the key to select and copy manually");
                      }
                    }}
                    data-testid="2fa-manual-key-copy"
                  >
                    {copied ? <Check size={15} className="mr-1.5" /> : <Copy size={15} className="mr-1.5" />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </div>

                <p className="flex items-start gap-1.5 text-xs text-blue-700">
                  <span aria-hidden className="font-semibold">ⓘ</span>
                  <span>Store this key securely. You'll need it if you can't scan the QR code.</span>
                </p>
              </div>
            )}

            <form onSubmit={handleVerify} className="space-y-3">
              <div>
                <label className="text-sm text-[#555555] block mb-1.5">
                  Enter the 6-digit code from your authenticator
                </label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="text-center text-lg tracking-[0.5em] font-mono h-12"
                  autoFocus
                  data-testid="2fa-code-input"
                />
              </div>

              {error && (
                <p className="text-sm text-red-600 text-center" data-testid="2fa-error">{error}</p>
              )}

              <Button
                type="submit"
                className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white"
                disabled={loading || code.length !== 6}
                data-testid="2fa-verify-btn"
              >
                {loading ? "Verifying..." : "Verify & Enable 2FA"}
                {!loading && <ArrowRight size={15} className="ml-2" />}
              </Button>
            </form>
          </div>
        )}

        {/* Step: Recovery Codes */}
        {step === "recovery" && (
          <div className="bg-white rounded-xl border border-[#E5E5E5] p-6 space-y-5" data-testid="2fa-recovery-step">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <KeyRound size={18} className="text-amber-600 shrink-0" />
              <p className="text-sm text-amber-800">
                Save these recovery codes securely. They cannot be shown again.
              </p>
            </div>

            <div className="bg-[#F5F5F5] rounded-lg p-4">
              <div className="grid grid-cols-2 gap-2" data-testid="recovery-codes-grid">
                {recoveryCodes.map((c, i) => (
                  <code
                    key={i}
                    className="text-sm font-mono text-[#555555] bg-white rounded px-3 py-1.5 border border-[#E5E5E5] text-center"
                    data-testid={`recovery-code-${i}`}
                  >
                    {c}
                  </code>
                ))}
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full"
              onClick={copyRecoveryCodes}
              data-testid="copy-recovery-codes-btn"
            >
              {copied ? <Check size={14} className="mr-2" /> : <Copy size={14} className="mr-2" />}
              {copied ? "Copied!" : "Copy all codes"}
            </Button>

            <Button
              className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white"
              onClick={handleComplete}
              data-testid="2fa-complete-btn"
            >
              Continue to login
              <ArrowRight size={15} className="ml-2" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
